<template>
    <pc-alert
        v-bind="validationAlertBinder"
        id="ValidationResultAlert">
        <div class="flex-grow-1">
            <pc-expand-list
                v-bind:header="errorHeader"
                v-bind:items="truncatedValidationErrors"
                hide-if-empty
                use-bullets />
            <pc-expand-list
                v-bind:header="warningHeader"
                v-bind:items="truncatedValidationWarnings"
                hide-if-empty
                use-bullets />
            <pc-expand-list
                v-bind:header="infoHeader"
                v-bind:items="truncatedValidationInformation"
                hide-if-empty
                use-bullets />
            <div v-if="largeErrorSetObjectUrl">
                <hr />
                <a
                    v-bind:href="largeErrorSetObjectUrl"
                    download="ValidationErrors.txt">
                    {{ validationDownloadLinkText }}
                </a>
            </div>
        </div>
    </pc-alert>
</template>

<script setup>
    import { ALERT_SEVERITY } from "Util/alerts";
    import { computed, inject } from "vue";
    import { KEYS, NAMESPACE } from "./i18n/externalDataShared.i18n";
    import { useResource } from "Util/i18n";
    import { PC_RES_SYMBOL } from "_Main/injectionSymbols";

    const res = inject(PC_RES_SYMBOL);

    const props = defineProps({
        validationModel: { type: Object, required: false, default: () => {} },
        shown: { type: Boolean, required: false, default: true }
    });

    const {
        errorHeader,
        warningHeader,
        infoHeader,
        validationSuccessTitle,
        validationFailureTitle,
        validationDownloadLinkText
    } = useResource(KEYS, NAMESPACE);

    const truncatedValidationErrors = computed(() =>
        truncateValidationMessages(props.validationModel.ValidationErrors)
    );
    const truncatedValidationWarnings = computed(() =>
        truncateValidationMessages(props.validationModel.ValidationWarnings)
    );
    const truncatedValidationInformation = computed(() =>
        truncateValidationMessages(props.validationModel.ValidationInformation)
    );
    const validationSuccess = computed(() => props.validationModel.ValidationSuccess);

    const MAX_MESSAGES = 1000;
    function truncateValidationMessages(messages) {
        if (!messages) {
            return null;
        }
        const truncated = messages.slice(0, MAX_MESSAGES); // will fall back to array length if shorter than max

        if (truncated.length < messages.length) {
            truncated.push(
                res({
                    namespace: NAMESPACE,
                    key: KEYS.validationAdditionalMessagesText,
                    values: { number: messages.length - truncated.length }
                })
            );
        }
        return truncated;
    }

    const validationAlertBinder = computed(() => {
        if (!props.shown) {
            return { shown: props.shown };
        }
        const severity = !validationSuccess.value
            ? ALERT_SEVERITY.error
            : truncatedValidationWarnings.value.length > 0
              ? ALERT_SEVERITY.warning
              : ALERT_SEVERITY.success;
        const title = validationSuccess.value ? validationSuccessTitle.value : validationFailureTitle.value;

        return {
            shown: props.shown,
            severity,
            title
        };
    });

    // If there are many errors, provide an option to download the validation set as a text file.
    // This is to avoid performance issues with rendering a large number of items in the alert.
    let textFile = null;
    const largeErrorSetObjectUrl = computed(() => {
        if (textFile) {
            URL.revokeObjectURL(textFile);
        }

        if (
            props.validationModel.ValidationErrors?.length < MAX_MESSAGES &&
            props.validationModel.ValidationWarnings?.length < MAX_MESSAGES &&
            props.validationModel.ValidationInformation?.length < MAX_MESSAGES
        ) {
            return null;
        }

        const blobParts = [];
        if (props.validationModel.ValidationErrors && props.validationModel.ValidationErrors.length > 0) {
            blobParts.push(`${errorHeader.value}\n`);
            props.validationModel.ValidationErrors.forEach((error) => {
                blobParts.push(` - ${error}\n`);
            });
        }
        if (props.validationModel.ValidationWarnings && props.validationModel.ValidationWarnings.length > 0) {
            blobParts.push(`${warningHeader.value}\n`);
            props.validationModel.ValidationWarnings.forEach((warning) => {
                blobParts.push(` - ${warning}\n`);
            });
        }
        if (props.validationModel.ValidationInformation && props.validationModel.ValidationInformation.length > 0) {
            blobParts.push(`${infoHeader.value}\n`);
            props.validationModel.ValidationInformation.forEach((info) => {
                blobParts.push(` - ${info}\n`);
            });
        }
        textFile = URL.createObjectURL(new Blob(blobParts, { type: "text/plain" }));
        return textFile;
    });
</script>
